// Types for our prompt tokens
export interface PromptToken {
  id: string;
  text: string;
  category: string;
  source: string;
  hasConflict: boolean;
  suggestion?: string;
}

export interface ConflictAnalysis {
  conflicts: Array<{
    tokenId: string;
    conflictType: string;
    explanation: string;
    suggestion: string;
  }>;
  isConsistent: boolean;
}

export interface EnhancedPrompt {
  tokens: PromptToken[];
  enhancedText: string;
  cinematic: boolean;
  resolution: string;
}

// Groq API response schema (simplified without zod for now)
interface GroqResponse {
  choices: Array<{
    message: {
      content: string;
    };
  }>;
}

// Local conflict rules for instant feedback
export const localConflictRules = {
  'night': ['sunlight', 'daylight', 'bright', 'sunny'],
  'rain': ['clear', 'sunny', 'dry', 'sunny'],
  'close-up': ['wide', 'background', 'landscape', 'panorama'],
  'indoor': ['outdoor', 'landscape', 'sky', 'nature'],
  'warm': ['cold', 'freezing', 'icy', 'winter'],
  'dark': ['bright', 'illuminated', 'sunny', 'daylight'],
  'cozy': ['sterile', 'clinical', 'cold', 'impersonal'],
  'peaceful': ['intense', 'dramatic', 'chaotic', 'violent'],
  'soft': ['harsh', 'dramatic', 'intense', 'sharp'],
  'natural': ['artificial', 'synthetic', 'digital', 'cyberpunk'],
};

// Groq API service
class GroqService {
  private apiKey: string;
  private baseUrl = 'https://api.groq.com/openai/v1/chat/completions';

  constructor() {
    this.apiKey = import.meta.env.VITE_GROQ_API_KEY || '';
    if (!this.apiKey) {
      console.warn('VITE_GROQ_API_KEY not found, using fallback logic');
    }
  }

  private async callGroq(messages: Array<{role: string; content: string}>): Promise<any> {
    if (!this.apiKey) {
      throw new Error('Groq API key not configured');
    }

    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          model: 'llama3-70b-8192',
          messages,
          temperature: 0.1,
          max_tokens: 2000,
        }),
      });

      if (!response.ok) {
        throw new Error(`Groq API error: ${response.statusText}`);
      }

      const data = await response.json();
      return data as GroqResponse;
    } catch (error) {
      console.error('Groq API call failed:', error);
      throw error;
    }
  }

  // Function 1: Real-time conflict detection
  async analyzeConflicts(tokens: PromptToken[]): Promise<ConflictAnalysis> {
    if (!this.apiKey) {
      return this.localConflictAnalysis(tokens);
    }

    try {
      const promptText = tokens.map(t => t.text).join(' ');
      
      const messages = [
        {
          role: 'system',
          content: `You are a professional Prompt Engineer. Analyze these prompt tokens for logical contradictions.
          
          Return ONLY a JSON object with this format:
          {
            "conflicts": [
              {
                "tokenId": "id_of_token",
                "conflictType": "type_of_conflict",
                "explanation": "why this is a conflict",
                "suggestion": "how to fix it"
              }
            ],
            "isConsistent": true/false
          }
          
          Common conflicts to detect:
          - Lighting contradictions (warm vs cold, soft vs harsh)
          - Setting contradictions (indoor vs outdoor)
          - Camera contradictions (close-up vs wide)
          - Mood contradictions (peaceful vs intense)
          - Time contradictions (night vs daylight)
          - Weather contradictions (rain vs clear)`
        },
        {
          role: 'user',
          content: `Analyze these tokens: ${JSON.stringify(tokens)}`
        }
      ];

      const response = await this.callGroq(messages);
      const content = response.choices[0].message.content;
      
      try {
        return JSON.parse(content);
      } catch (parseError) {
        console.error('Failed to parse Groq response:', parseError);
        return this.localConflictAnalysis(tokens);
      }
    } catch (error) {
      console.error('Groq conflict analysis failed, using local fallback:', error);
      return this.localConflictAnalysis(tokens);
    }
  }

  // Function 2: Prompt enhancement
  async enhancePrompt(tokens: PromptToken[]): Promise<EnhancedPrompt> {
    if (!this.apiKey) {
      return this.localEnhancement(tokens);
    }

    try {
      const promptText = tokens.map(t => t.text).join(' ');
      
      const messages = [
        {
          role: 'system',
          content: `You are a professional Prompt Engineer specializing in cinematic, 8k, professional prompts.
          
          Your job is to ENHANCE the prompt with rich filmmaking details while NEVER changing the
          user's intent, aspect ratio, subject, or framing.  Keep the spirit of the original
          sentence intact and only elaborate; do NOT inject new subjects or alter the core scene.
          
          Rewrite the user's prompt to be:
          - Cinematic and visually stunning
          - 8k resolution quality
          - Professional and technically precise
          - Resolve all logical conflicts
          - Maintain the user's core intent exactly
          
          Add camera models, lens details, film stock/sensor look, lighting rigs, color grading,
          composition techniques, atmosphere effects, texture and movement where appropriate.
          Avoid generic filler and keep language tight.
          
          Return ONLY a JSON object with this format:
          {
            "enhancedText": "the rewritten prompt",
            "cinematic": true,
            "resolution": "8k",
            "tokens": [
              {
                "id": "original_id",
                "text": "rewritten_text",
                "category": "category",
                "source": "enhanced",
                "hasConflict": false,
                "suggestion": ""
              }
            ]
          }`
        },
        {
          role: 'user',
          content: `Enhance this prompt: ${promptText}`
        }
      ];

      const response = await this.callGroq(messages);
      const content = response.choices[0].message.content;
      
      try {
        return JSON.parse(content);
      } catch (parseError) {
        console.error('Failed to parse Groq enhancement response:', parseError);
        return this.localEnhancement(tokens);
      }
    } catch (error) {
      console.error('Groq enhancement failed, using local fallback:', error);
      return this.localEnhancement(tokens);
    }
  }

  // Local fallback for conflict detection
  private localConflictAnalysis(tokens: PromptToken[]): ConflictAnalysis {
    const conflicts: Array<{
      tokenId: string;
      conflictType: string;
      explanation: string;
      suggestion: string;
    }> = [];
    
    tokens.forEach(token => {
      const text = token.text.toLowerCase();
      
      Object.entries(localConflictRules).forEach(([key, conflictList]) => {
        if (text.includes(key)) {
          conflictList.forEach(conflict => {
            if (text.includes(conflict)) {
              conflicts.push({
                tokenId: token.id,
                conflictType: `${key}_vs_${conflict}`,
                explanation: `${key} conflicts with ${conflict}`,
                suggestion: `Replace ${conflict} with something compatible with ${key}`
              });
            }
          });
        }
      });
    });

    return {
      conflicts,
      isConsistent: conflicts.length === 0
    };
  }

  // Local fallback for enhancement
  private localEnhancement(tokens: PromptToken[]): EnhancedPrompt {
    const enhancedTokens = tokens.map(token => ({
      ...token,
      hasConflict: false,
      suggestion: '',
      source: 'enhanced'
    }));

    return {
      tokens: enhancedTokens,
      enhancedText: enhancedTokens.map(t => t.text).join(' '),
      cinematic: true,
      resolution: '8k'
    };
  }

  // Initial prompt processor
  async processInitialPrompt(description: string): Promise<PromptToken[]> {
    if (!this.apiKey) {
      return this.simpleTokenization(description);
    }

    try {
      const messages = [
        {
          role: 'system',
          content: `Parse this simple description into structured prompt tokens.
          
          Return ONLY a JSON array with this format:
          [
            {
              "id": "unique_id",
              "text": "token_text",
              "category": "subject|lighting|camera|mood|setting|technical",
              "source": "parsed",
              "hasConflict": false,
              "suggestion": ""
            }
          ]
          
          Categories:
          - subject: main characters/objects
          - lighting: light sources and quality
          - camera: shot type and angle
          - mood: emotional tone
          - setting: location and environment
          - technical: technical specifications`
        },
        {
          role: 'user',
          content: `Parse this description: ${description}`
        }
      ];

      const response = await this.callGroq(messages);
      const content = response.choices[0].message.content;
      
      try {
        return JSON.parse(content);
      } catch (parseError) {
        console.error('Failed to parse Groq tokenization response:', parseError);
        return this.simpleTokenization(description);
      }
    } catch (error) {
      console.error('Groq tokenization failed, using simple fallback:', error);
      return this.simpleTokenization(description);
    }
  }

  // Simple local tokenization fallback
  private simpleTokenization(description: string): PromptToken[] {
    const words = description.split(' ');
    return words.map((word, index) => ({
      id: `token_${index}`,
      text: word,
      category: 'subject',
      source: 'parsed',
      hasConflict: false,
      suggestion: ''
    }));
  }
}

export const groqService = new GroqService();
